import { ReactElement, useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { useResultsStore } from '../../../store/ResultsStore'
import { InfiniteScroller } from '../../../../../../src/ui-components/scroll/InfiniteScroller'
import styled from 'styled-components'
import { breakpoints } from '../../../../../../src/theme/breakpoints'
import Group from './Group'
import { NonCachedSearchType, ProductModel } from 'interfaces'
import { I18n, StorageKeys } from 'common'

interface Props {
  parts: ProductModel[]
}

const TableWrapper = observer((props: Props): ReactElement => {
  const { parts } = props
  const resultsStore = useResultsStore()
  const lastResultRef = useRef(null)
  const isRestoringScroll = useRef(
    !!sessionStorage.getItem(StorageKeys.SCROLL_TO_PRODUCTKEY)
  )
  const { showInterchangeDisclaimers } = resultsStore

  const showInfiniteScroller =
    !showInterchangeDisclaimers && !isRestoringScroll.current

  useEffect(() => {
    const productKey = sessionStorage.getItem(StorageKeys.SCROLL_TO_PRODUCTKEY)
    if (!productKey || !parts.length) return

    const isInterchangeSearch =
      resultsStore.lastNonCachedSearch ===
      NonCachedSearchType.searchByInterchange

    // For interchange searches, wait for results to be fully loaded before scrolling
    if (
      isInterchangeSearch &&
      (!resultsStore.searchResults.isSnwFinished || resultsStore.isLoading)
    ) {
      return
    }

    const clearScrollState = () => {
      sessionStorage.removeItem(StorageKeys.SCROLL_TO_PRODUCTKEY)
      isRestoringScroll.current = false
    }

    try {
      const el = document.querySelector(`[data-product-key="${productKey}"]`)
      if (el) {
        clearScrollState()
        setTimeout(
          () => el.scrollIntoView({ block: 'center', behavior: 'smooth' }),
          100
        )
      } else if (resultsStore.hasMoreResults && !resultsStore.isLoading) {
        resultsStore.loadNextPage(false)
      } else if (
        !resultsStore.hasMoreResults &&
        (!isInterchangeSearch || !resultsStore.isLoading)
      ) {
        clearScrollState()
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in querySelector:', error)
      clearScrollState()
    }
  }, [
    parts,
    resultsStore.hasMoreResults,
    resultsStore.isLoading,
    resultsStore,
    resultsStore.searchResults.isSnwFinished,
  ])

  const groupedParts = groupParts(parts)
  const groups = Object.entries(groupedParts)

  return (
    <div>
      {showInfiniteScroller && (
        <InfiniteScroller
          hasMore={resultsStore.hasMoreResults}
          pxThreshold={500}
          loadMore={() => resultsStore.loadNextPage(false)}
          lastResultRef={lastResultRef}
        />
      )}

      <Groups>
        {groups?.map(([groupName, parts]) => (
          <Group key={groupName?.trim()} groupName={groupName} parts={parts} />
        ))}
      </Groups>

      {showInfiniteScroller && (
        <div>
          <div ref={lastResultRef} />
          {!resultsStore.hasMoreResults && (
            <EndOfResults>
              <I18n name="noMoreResults" />{' '}
            </EndOfResults>
          )}
        </div>
      )}
    </div>
  )
})

/**
 * Group parts by its group attribute, if undefined then a single group will be created
 * @param array
 * @returns
 */
const groupParts = (array: ProductModel[]): Record<string, ProductModel[]> => {
  return array.reduce(
    (acc, part) => {
      const { heavyDutyGroup } = part
      const groupName = heavyDutyGroup?.manufacturer + heavyDutyGroup?.partType
      const key = groupName ?? 'undefined'

      if (!acc[key]) acc[key] = []

      acc[key].push(part)
      return acc
    },
    {} as Record<string, ProductModel[]>
  )
}

const EndOfResults = styled.div`
  font-family: 'Roboto', serif;
  margin: 0px 0px 20px 0px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`

const Groups = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 0px 0px 20px 0px;
  min-width: 0;

  @media screen and (min-width: ${breakpoints.tablet}px) and (max-width: ${breakpoints.laptopMedium -
    1}px) {
    overflow-x: auto;
  }
`

export default TableWrapper
